import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { CurrencySelect } from "components/CurrencySelect";
import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";

import {
  CHIP_VARIANTS,
  getOptionLabel,
  isOptionEqualToValue,
  renderChip,
  renderEmailOption,
  renderPartnerOption,
  renderStringOption,
  THEME_COLORS,
  TYPOGRAPHY_VARIANTS,
} from "../UnifiedPartnerSelector/utils";

import type { DirectBookingAssignmentFormState } from "types/shipment";

import type { AssigneeStatus } from "./utils";
import type { NetworkMember, PartnerSelectionItem } from "../../types";

interface Props {
  selectedItem: PartnerSelectionItem | null;
  options: PartnerSelectionItem[];
  networkMembers: NetworkMember[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onSelectionChange: (newSelections: (string | PartnerSelectionItem)[]) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  onBlur: () => void;
  inputError: string | null;
  assigneeStatus: AssigneeStatus | null;
  assignment: DirectBookingAssignmentFormState;
  onPriceChange: (price: string) => void;
  onCurrencyChange: (currency: string) => void;
  priceLabel: string;
  priceHelperText: string | null;
  priceError: string | null;
  loadingNetwork: boolean;
}

function AssigneeStatusLine(props: { status: AssigneeStatus }) {
  const { status } = props;
  if (status.kind === "checking") {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={14} />
        <Typography
          variant={TYPOGRAPHY_VARIANTS.BODY2}
          color={THEME_COLORS.TEXT_SECONDARY}
        >
          {status.message}
        </Typography>
      </Stack>
    );
  }
  if (status.kind === "error") {
    return <ErrorTypography error={status.message} />;
  }
  return (
    <Typography
      variant={TYPOGRAPHY_VARIANTS.BODY2}
      color={THEME_COLORS.TEXT_SECONDARY}
    >
      {status.message}
    </Typography>
  );
}

export function DirectBookingAssigneeSelector(props: Props) {
  const {
    selectedItem,
    options,
    networkMembers,
    inputValue,
    onInputChange,
    onSelectionChange,
    onKeyDown,
    onBlur,
    inputError,
    assigneeStatus,
    assignment,
    onPriceChange,
    onCurrencyChange,
    priceLabel,
    priceHelperText,
    priceError,
    loadingNetwork,
  } = props;

  const selectedItems = selectedItem === null ? [] : [selectedItem];

  return (
    <LoadingWrappedNode loading={loadingNetwork}>
      <Stack spacing={1}>
        <Autocomplete<PartnerSelectionItem | string, true, false, true>
          multiple
          value={selectedItems}
          onChange={(_, newValue) => onSelectionChange(newValue)}
          options={options}
          getOptionLabel={getOptionLabel}
          inputValue={inputValue}
          onInputChange={(_, value) => onInputChange(value)}
          filterSelectedOptions
          freeSolo
          isOptionEqualToValue={isOptionEqualToValue}
          renderOption={(optionProps, option) => {
            if (typeof option === "string") {
              return renderStringOption(optionProps, option);
            }
            if (option.type === "partner") {
              return renderPartnerOption(optionProps, option, networkMembers);
            }
            if (option.type === "email") {
              return renderEmailOption(optionProps, option);
            }
            return null;
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => renderChip(option, index, getTagProps))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                selectedItem === null
                  ? "Type the carrier's name or email..."
                  : ""
              }
              variant={CHIP_VARIANTS.OUTLINED}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              error={inputError !== null}
              helperText={inputError}
            />
          )}
        />
        {assigneeStatus !== null && (
          <AssigneeStatusLine status={assigneeStatus} />
        )}
        <Typography
          variant={TYPOGRAPHY_VARIANTS.BODY2}
          color={THEME_COLORS.TEXT_SECONDARY}
        >
          This shipment will show as booked with this carrier at the rate below.
        </Typography>
        {/*
        CurrencySelect carries a width of its own (100%, min 160), so both have
        to be overridden here or it takes the row and leaves the price nothing.
        A Select's inner element carries a 1.4375em min-height and a bare input
        does not, so the price needs it too to share a baseline with the
        currency beside it.
        */}
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            value={assignment.price}
            onChange={(event) => onPriceChange(event.target.value)}
            label={priceLabel}
            variant={CHIP_VARIANTS.OUTLINED}
            error={priceError !== null}
            helperText={priceError ?? priceHelperText}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            sx={{ flex: 1, "& .MuiInputBase-input": { minHeight: "1.4375em" } }}
          />
          <CurrencySelect
            value={assignment.currency}
            onChange={onCurrencyChange}
            sx={{ width: 120, minWidth: 120, flexShrink: 0 }}
          />
        </Stack>
      </Stack>
    </LoadingWrappedNode>
  );
}
